import { fmtDateTime } from "@/lib/dateFormat";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Plus, CalendarDays, ChevronLeft, ChevronRight, User, X, Check, Ban, Pencil, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, EmptyState } from "@/components/States";
import { NumberPlate } from "@/components/NumberPlate";
import { StatusChip } from "@/components/StatusChip";
import { WhatsAppSendDialog } from "@/components/WhatsAppSendDialog";
import { WhatsAppLog } from "@/components/WhatsAppLog";
import { MessageCircle } from "lucide-react";
import { waBookingText } from "@/lib/whatsapp";
import { useAuth } from "@/lib/auth";
import { OCCUPANCY_STATUSES, occupancyLabel, categoryLabel, priorityLabel } from "@/lib/workCategories";
import { UkDatePicker } from "@/components/UkDatePicker";
import { toast } from "sonner";

const workItemsList = (b) => {
  if (Array.isArray(b.work_items) && b.work_items.length) {
    return b.work_items.map((w) => categoryLabel(w.category) + (w.subcategory ? ` · ${w.subcategory}` : ""));
  }
  if (b.work_category) return [categoryLabel(b.work_category) + (b.subcategory ? ` · ${b.subcategory}` : "")];
  return [];
};
const priorityBadge = (p) => {
  if (!p || p === "normal") return null;
  const cls = p === "emergency" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{priorityLabel(p)}</span>;
};

const VIEWS = ["month", "week", "day"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const sl = (b) => b.start_local || b.start_at || "";
const timeOf = (s) => (s || "").slice(11, 16) || "--:--";
const friendly = (s) => fmtDateTime(s, s || "—");

export default function Calendar() {
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link from a dashboard notification: /calendar?booking=<id> opens that booking.
  useEffect(() => {
    const bid = searchParams.get("booking");
    if (!bid) return;
    (async () => {
      try {
        const all = await api("/bookings");
        const match = (all || []).find((x) => x.id === bid);
        if (match) setSelected(match);
      } catch { /* ignore */ }
      finally { searchParams.delete("booking"); setSearchParams(searchParams, { replace: true }); }
    })();
  }, []);

  const range = useMemo(() => {
    if (view === "day") return { from: new Date(anchor), to: addDays(anchor, 1) };
    if (view === "week") { const s = startOfWeek(anchor); return { from: s, to: addDays(s, 7) }; }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return { from: gridStart, to: addDays(gridStart, 42) };
  }, [view, anchor]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api(`/bookings/calendar?date_from=${range.from.toISOString()}&date_to=${range.to.toISOString()}`);
      setBookings(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [range.from.getTime(), range.to.getTime()]);

  const byDate = useMemo(() => bookings.reduce((acc, b) => {
    const startD = sl(b).slice(0, 10);
    if (b.booking_type === "multi_day" && (b.end_local || b.end_at)) {
      const endD = (b.end_local || b.end_at).slice(0, 10);
      let cur = new Date(`${startD}T00:00:00`);
      const end = new Date(`${endD}T00:00:00`);
      let guard = 0;
      while (cur <= end && guard < 120) {
        const key = iso(cur);
        (acc[key] = acc[key] || []).push({ ...b, _spanStart: key === startD, _spanEnd: key === endD });
        cur = addDays(cur, 1); guard += 1;
      }
    } else {
      (acc[startD] = acc[startD] || []).push(b);
    }
    return acc;
  }, {}), [bookings]);

  const label = view === "month"
    ? anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : view === "week"
      ? `${startOfWeek(anchor).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${addDays(startOfWeek(anchor), 6).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
      : anchor.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const step = (dir) => {
    if (view === "month") setAnchor((a) => addMonths(a, dir));
    else if (view === "week") setAnchor((a) => addDays(a, dir * 7));
    else setAnchor((a) => addDays(a, dir));
  };

  return (
    <div>
      <PageHeader title="Calendar" subtitle="Bookings across your workshop"
        actions={<Link to="/booking-new" data-testid="new-booking-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" />New Booking</Link>} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border bg-card p-1" data-testid="calendar-view-toggle">
          {VIEWS.map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
              className={`rounded-md px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${view === v ? "bg-brand text-white" : "text-slate-500 hover:bg-secondary"}`}>{v}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => step(-1)} data-testid="cal-prev" className="rounded-lg border border-border bg-card p-2 text-midnight hover:bg-secondary"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[180px] text-center font-display text-base font-bold text-midnight" data-testid="cal-label">{label}</span>
          <button onClick={() => step(1)} data-testid="cal-next" className="rounded-lg border border-border bg-card p-2 text-midnight hover:bg-secondary"><ChevronRight className="h-4 w-4" /></button>
          <button onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); }} data-testid="cal-today" className="rounded-lg border border-border bg-card px-3 py-2 text-xs font-bold uppercase text-brand-2 hover:bg-secondary">Today</button>
        </div>
      </div>

      {loading ? <Loading /> : (
        view === "month" ? <MonthView anchor={anchor} byDate={byDate} onPickDay={(d) => { setAnchor(d); setView("day"); }} onSelect={setSelected} />
          : view === "week" ? <WeekView anchor={anchor} byDate={byDate} onSelect={setSelected} />
            : <DayView anchor={anchor} list={byDate[iso(anchor)] || []} onSelect={setSelected} />
      )}

      {selected && <BookingModal booking={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); load(); }} />}
    </div>
  );
}

function PriorityDot({ p }) {
  if (!p || p === "normal") return null;
  return <span className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${p === "emergency" ? "bg-red-500" : "bg-amber-500"}`} />;
}

function BookingChip({ b, compact, onSelect }) {
  if (b.booking_type === "multi_day") {
    const arrival = (b.start_local || b.start_at || "").slice(0, 10);
    const collect = (b.end_local || b.end_at || "").slice(0, 10);
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(b); }} data-testid={`booking-${b.id}`}
        className={`block w-full border-y border-amber-400/60 bg-amber-100/80 px-1.5 py-1 text-left transition-colors hover:bg-amber-200/80 ${b._spanStart ? "rounded-l-md border-l" : ""} ${b._spanEnd ? "rounded-r-md border-r" : ""} ${compact ? "" : "mb-1"}`}>
        <p className="truncate text-[11px] font-bold text-amber-800"><PriorityDot p={b.priority} />{b.vehicle?.vrm || "Vehicle"}{b._spanStart ? " · IN" : b._spanEnd ? " · OUT" : ""}</p>
        {!compact && <p className="truncate text-[10px] text-amber-700">{b.reason} · {arrival}→{collect}</p>}
      </button>
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(b); }} data-testid={`booking-${b.id}`}
      className={`block w-full rounded-md border border-brand/30 bg-brand-tertiary/60 px-1.5 py-1 text-left transition-colors hover:bg-brand-tertiary ${compact ? "" : "mb-1"}`}>
      <p className="truncate text-[11px] font-bold text-brand-2"><PriorityDot p={b.priority} />{timeOf(sl(b))} {b.vehicle?.vrm || ""}</p>
      {!compact && <p className="truncate text-[11px] text-midnight">{b.reason}</p>}
      <p className="truncate text-[10px] text-muted-foreground">{b.customer?.name || "—"}{b.assignee ? ` · ${b.assignee.name}` : ""}</p>
    </button>
  );
}

function MonthView({ anchor, byDate, onPickDay, onSelect }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const month = anchor.getMonth();
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="month-grid">
      <div className="grid grid-cols-7 border-b border-border bg-secondary">
        {DOW.map((d) => <div key={d} className="px-2 py-2 text-center text-[11px] font-extrabold uppercase tracking-wide text-muted-foreground">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const list = byDate[iso(d)] || [];
          const inMonth = d.getMonth() === month;
          const isToday = iso(d) === iso(new Date());
          return (
            <div key={iso(d)} role="button" tabIndex={0} onClick={() => onPickDay(d)} onKeyDown={(e) => { if (e.key === "Enter") onPickDay(d); }} data-testid={`month-day-${iso(d)}`}
              className={`min-h-[96px] cursor-pointer border-b border-r border-border p-1.5 text-left align-top transition-colors hover:bg-secondary ${inMonth ? "" : "bg-secondary/40"}`}>
              <span className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${isToday ? "bg-brand text-white" : inMonth ? "text-midnight" : "text-muted-foreground"}`}>{d.getDate()}</span>
              <div className="space-y-1">
                {list.slice(0, 3).map((b) => <BookingChip key={b.id} b={b} compact onSelect={onSelect} />)}
                {list.length > 3 && <p className="text-[10px] font-bold text-brand-2">+{list.length - 3} more</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ anchor, byDate, onSelect }) {
  const s = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
  return (
    <div className="grid gap-3 md:grid-cols-7" data-testid="week-grid">
      {days.map((d) => {
        const list = byDate[iso(d)] || [];
        const isToday = iso(d) === iso(new Date());
        return (
          <div key={iso(d)} className="rounded-xl border border-border bg-card p-2" data-testid={`week-day-${iso(d)}`}>
            <p className={`mb-2 text-center text-[11px] font-extrabold uppercase ${isToday ? "text-brand-2" : "text-muted-foreground"}`}>{d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" })}</p>
            {list.length === 0 ? <p className="py-2 text-center text-[11px] text-muted-foreground">—</p> : list.map((b) => <BookingChip key={b.id} b={b} onSelect={onSelect} />)}
          </div>
        );
      })}
    </div>
  );
}

function DayView({ list, onSelect }) {
  if (list.length === 0) return <EmptyState icon={CalendarDays} title="No bookings this day" action={<Link to="/booking-new" className="rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white">New Booking</Link>} />;
  return (
    <div className="space-y-2" data-testid="day-list">
      {list.map((b) => (
        <button type="button" key={b.id} onClick={() => onSelect(b)} data-testid={`booking-${b.id}`} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-brand">
          <div className="w-16 shrink-0 text-center"><p className="font-display text-lg font-bold text-midnight">{timeOf(sl(b))}</p><p className="text-[10px] text-muted-foreground">{b.duration_minutes}m</p></div>
          {b.vehicle?.vrm && <NumberPlate vrm={b.vehicle.vrm} size="sm" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-midnight">{b.reason}</p>
            <p className="truncate text-xs text-muted-foreground">{b.vehicle ? `${b.vehicle.make || ""} ${b.vehicle.model || ""}` : ""} · {b.customer?.name || "No customer"}</p>
          </div>
          <StatusChip status={b.display_status || "booked"} />
          <div className="flex items-center gap-1 text-xs font-semibold text-brand-2" data-testid={`booking-assignee-${b.id}`}>
            <User className="h-3.5 w-3.5" />{b.assignee ? b.assignee.name : "Unassigned"}
          </div>
        </button>
      ))}
    </div>
  );
}

function BookingModal({ booking, onClose, onChanged }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [quoteAmt, setQuoteAmt] = useState(booking.quoted_price ?? "");
  const [quoteNote, setQuoteNote] = useState("");
  const local = sl(booking);
  const isMulti = booking.booking_type === "multi_day";
  const [form, setForm] = useState({ date: local.slice(0, 10), time: local.slice(11, 16), duration_minutes: booking.duration_minutes, reason: booking.reason || "", end_date: (booking.end_local || booking.end_at || "").slice(0, 10), end_time: (booking.end_local || booking.end_at || "").slice(11, 16) || "17:00" });
  const [waOpen, setWaOpen] = useState(false);
  const [waRefresh, setWaRefresh] = useState(0);

  const act = async (body, force) => {
    setBusy(true);
    try {
      await api(`/bookings/${booking.id}${force ? "?force=true" : ""}`, { method: "PATCH", body: JSON.stringify(body) });
      toast.success("Booking updated");
      onChanged();
    } catch (e) {
      if (e.status === 409 && e.body?.detail?.code === "overlap") {
        if (window.confirm(e.body.detail.message)) { await act(body, true); return; }
      } else { toast.error(e.message); }
    } finally { setBusy(false); }
  };

  const saveEdit = () => {
    if (!form.reason.trim() || !form.date) { toast.error("Date and reason required"); return; }
    const body = { start_at: `${form.date}T${form.time}:00`, duration_minutes: Number(form.duration_minutes), reason: form.reason.trim() };
    if (isMulti && form.end_date) body.end_at = `${form.end_date}T${form.end_time || "17:00"}:00`;
    act(body);
  };

  const del = async () => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    setBusy(true);
    try { await api(`/bookings/${booking.id}`, { method: "DELETE" }); toast.success("Booking deleted"); onChanged(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const active = booking.display_status !== "completed" && booking.display_status !== "cancelled";
  const needsQuote = booking.source === "online" && (booking.pricing_mode === "from" || booking.pricing_mode === "quote");

  const proposeQuote = async () => {
    const amt = Number(quoteAmt);
    if (!(amt >= 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api(`/bookings/${booking.id}/quote`, { method: "POST", body: JSON.stringify({ amount: amt, note: quoteNote.trim() }) });
      toast.success("Quote sent to the customer");
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const resolveChange = async (decision) => {
    setBusy(true);
    try {
      await api(`/bookings/${booking.id}/change/${decision}`, { method: "POST" });
      toast.success(decision === "accept" ? "Booking rescheduled to the requested time" : "Change request rejected — original time kept");
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const createJob = async () => {
    setBusy(true);
    try {
      const res = await api(`/bookings/${booking.id}/create-job`, { method: "POST" });
      toast.success(res.already_linked ? "Opening the linked job" : "Job created from booking");
      if (res.job_id) navigate(`/jobs/${res.job_id}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="booking-modal">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusChip status={booking.display_status || "booked"} />
            <span className="font-display text-base font-bold text-midnight">{timeOf(local)} · {booking.duration_minutes}m</span>
          </div>
          <button onClick={onClose} data-testid="booking-modal-close" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"><X className="h-4 w-4" /></button>
        </div>

        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><span className="mb-1 block text-[10px] font-bold tracking-widest text-muted-foreground">DATE</span>
                <UkDatePicker value={form.date} onChange={(d) => setForm((f) => ({ ...f, date: d }))} testId="edit-booking-date" /></div>
              <label className="block"><span className="mb-1 block text-[10px] font-bold tracking-widest text-muted-foreground">TIME</span>
                <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="edit-booking-time" className="w-full rounded-lg border border-input px-3 py-2 text-sm" /></label>
            </div>
            <label className="block"><span className="mb-1 block text-[10px] font-bold tracking-widest text-muted-foreground">DURATION (MIN)</span>
              <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="edit-booking-duration" className="w-full rounded-lg border border-input px-3 py-2 text-sm" /></label>
            {isMulti && (
              <div className="grid grid-cols-2 gap-2">
                <div><span className="mb-1 block text-[10px] font-bold tracking-widest text-muted-foreground">COLLECTION DATE</span>
                  <UkDatePicker value={form.end_date} min={form.date} onChange={(d) => setForm((f) => ({ ...f, end_date: d }))} testId="edit-booking-collection-date" /></div>
                <label className="block"><span className="mb-1 block text-[10px] font-bold tracking-widest text-muted-foreground">COLLECTION TIME</span>
                  <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="edit-booking-collection-time" className="w-full rounded-lg border border-input px-3 py-2 text-sm" /></label>
              </div>
            )}
            <label className="block"><span className="mb-1 block text-[10px] font-bold tracking-widest text-muted-foreground">REASON</span>
              <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="edit-booking-reason" className="w-full rounded-lg border border-input px-3 py-2 text-sm" /></label>
            <div className="flex gap-2 pt-1">
              <button onClick={saveEdit} disabled={busy} data-testid="edit-booking-save" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand py-2.5 text-sm font-bold text-white disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>
              <button onClick={() => setEditing(false)} className="rounded-lg border border-border px-4 py-2.5 text-sm font-bold text-midnight">Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2.5">
              {booking.vehicle?.vrm && <div><NumberPlate vrm={booking.vehicle.vrm} size="sm" /></div>}
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-base font-bold text-midnight">{booking.reason}</p>
                {priorityBadge(booking.priority)}
                {booking.source === "online" && <span data-testid="booking-online-badge" className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">ONLINE</span>}
              </div>
              {workItemsList(booking).length > 0 && (
                <div data-testid="booking-categories">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted-foreground">Work categories</span>
                  <div className="flex flex-wrap gap-1.5">
                    {workItemsList(booking).map((t, i) => (
                      <span key={i} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-semibold text-midnight">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              <Row label="Vehicle" value={booking.vehicle ? `${booking.vehicle.make || ""} ${booking.vehicle.model || ""}`.trim() || "—" : "—"} />
              <Row label="Customer" value={booking.customer?.name || booking.customer_name_online || "No customer"} />
              {booking.source === "online" && booking.customer_phone_online && <Row label="Phone" value={booking.customer_phone_online} />}
              {needsQuote && (
                <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3" data-testid="quote-section">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-brand-2">Pricing · {booking.pricing_mode === "from" ? "Starting from" : "Quote required"}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{booking.price_display}</p>
                  {booking.quote_status === "accepted" && <p className="mt-1 text-sm font-bold text-success" data-testid="quote-accepted">Customer accepted £{Number(booking.quoted_price).toFixed(2)}</p>}
                  {booking.quote_status === "declined" && <p className="mt-1 text-sm font-bold text-danger" data-testid="quote-declined">Customer declined the quote (£{Number(booking.quoted_price).toFixed(2)})</p>}
                  {booking.quote_status === "proposed" && <p className="mt-1 text-sm font-bold text-warning" data-testid="quote-proposed">Quote £{Number(booking.quoted_price).toFixed(2)} sent — awaiting customer response</p>}
                  {active && booking.quote_status !== "accepted" && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input type="number" step="0.01" min="0" value={quoteAmt} onChange={(e) => setQuoteAmt(e.target.value)} data-testid="quote-amount" placeholder="£ amount" className="w-28 rounded-lg border border-input px-3 py-2 text-sm" />
                      <input value={quoteNote} onChange={(e) => setQuoteNote(e.target.value)} data-testid="quote-note" placeholder="Note (optional)" className="min-w-[120px] flex-1 rounded-lg border border-input px-3 py-2 text-sm" />
                      <button onClick={proposeQuote} disabled={busy} data-testid="quote-send-btn" className="rounded-lg bg-brand px-3 py-2 text-sm font-bold text-white disabled:opacity-60">{booking.quote_status === "proposed" ? "Resend" : "Send quote"}</button>
                    </div>
                  )}
                </div>
              )}
              <Row label="Assigned to" value={booking.assignee?.name || "Unassigned"} />
              {booking.job_id ? (
                <button onClick={() => navigate(`/jobs/${booking.job_id}`)} data-testid="booking-view-job" className="flex w-full items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2 text-left text-sm hover:border-brand">
                  <span className="text-muted-foreground">Linked job</span>
                  <span className="font-semibold text-brand-2">{booking.job?.title || "View job"} →</span>
                </button>
              ) : (
                <button onClick={createJob} disabled={busy} data-testid="booking-create-job" className="flex w-full items-center justify-center gap-2 rounded-lg border border-brand bg-brand-tertiary py-2.5 text-sm font-bold text-brand-2 hover:bg-brand-tertiary/70 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Create Job from Booking
                </button>
              )}
              {booking.notes ? <Row label="Notes" value={booking.notes} /> : null}

              {isMulti && (
                <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3" data-testid="multiday-section">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Multi-day workshop stay</p>
                  <div className="mt-1.5 space-y-1.5">
                    <Row label="Arrival" value={friendly(booking.start_local || booking.start_at)} />
                    <Row label="Collection" value={friendly(booking.end_local || booking.end_at)} />
                    {booking.workshop_bay && <Row label="Bay" value={booking.workshop_bay} />}
                    <Row label="Vehicle status" value={occupancyLabel(booking.occupancy_status) || "Not Arrived"} />
                  </div>
                  {active && (
                    <label className="mt-2 block"><span className="mb-1 block text-[10px] font-bold tracking-widest text-amber-700">UPDATE VEHICLE STATUS</span>
                      <select value={booking.occupancy_status || "not_arrived"} onChange={(e) => act({ occupancy_status: e.target.value })} disabled={busy} data-testid="occupancy-select" className="w-full rounded-lg border border-input px-3 py-2 text-sm">
                        {OCCUPANCY_STATUSES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select></label>
                  )}
                  {active && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button onClick={() => act({ occupancy_status: "vehicle_received" })} disabled={busy} data-testid="mark-received" className="rounded-lg bg-brand-tertiary px-3 py-1.5 text-xs font-bold text-brand-2">Vehicle received</button>
                      <button onClick={() => act({ occupancy_status: "in_workshop" })} disabled={busy} data-testid="mark-in-workshop" className="rounded-lg bg-brand-tertiary px-3 py-1.5 text-xs font-bold text-brand-2">In workshop</button>
                      <button onClick={() => act({ occupancy_status: "awaiting_collection" })} disabled={busy} data-testid="mark-ready" className="rounded-lg bg-brand-tertiary px-3 py-1.5 text-xs font-bold text-brand-2">Awaiting collection</button>
                      <button onClick={() => act({ occupancy_status: "vehicle_collected" })} disabled={busy} data-testid="mark-collected" className="rounded-lg bg-success/10 px-3 py-1.5 text-xs font-bold text-success">Collected</button>
                    </div>
                  )}
                </div>
              )}

              {booking.change_requested && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="change-request-section">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Customer requested a change</p>
                  <p className="mt-1 text-sm text-slate-700">Original: <span className="font-bold text-midnight">{friendly(booking.start_local || booking.start_at)}</span></p>
                  <p className="text-sm text-slate-700">Requested: <span className="font-bold text-midnight" data-testid="change-requested-time">{friendly(booking.requested_start_local || booking.requested_start_at)}</span></p>
                  {booking.change_note && <p className="mt-1 text-xs text-slate-500">“{booking.change_note}”</p>}
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => resolveChange("accept")} disabled={busy} data-testid="change-accept-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-success/10 py-2 text-sm font-bold text-success disabled:opacity-60"><Check className="h-4 w-4" />Accept &amp; reschedule</button>
                    <button onClick={() => resolveChange("reject")} disabled={busy} data-testid="change-reject-btn" className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-danger-bg py-2 text-sm font-bold text-danger disabled:opacity-60"><Ban className="h-4 w-4" />Reject</button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
              {booking.display_status === "pending" && (
                <>
                  <button onClick={() => act({ status: "booked" })} disabled={busy} data-testid="booking-approve-btn" className="flex items-center justify-center gap-2 rounded-lg bg-success/10 py-2.5 text-sm font-bold text-success"><Check className="h-4 w-4" />Approve</button>
                  <button onClick={() => act({ status: "declined" })} disabled={busy} data-testid="booking-decline-btn" className="flex items-center justify-center gap-2 rounded-lg bg-danger-bg py-2.5 text-sm font-bold text-danger"><Ban className="h-4 w-4" />Decline</button>
                  <button onClick={() => setEditing(true)} disabled={busy} data-testid="booking-edit-btn" className="flex items-center justify-center gap-2 rounded-lg border border-border py-2.5 text-sm font-bold text-midnight"><Pencil className="h-4 w-4" />Reschedule</button>
                  {isOwner && <button onClick={del} disabled={busy} data-testid="booking-delete-btn" className="flex items-center justify-center gap-2 rounded-lg bg-secondary py-2.5 text-sm font-bold text-midnight"><Trash2 className="h-4 w-4" />Delete</button>}
                </>
              )}
              {active && booking.display_status !== "pending" && (
                <>
                  <button onClick={() => act({ status: "completed" })} disabled={busy} data-testid="booking-complete-btn" className="flex items-center justify-center gap-2 rounded-lg bg-success/10 py-2.5 text-sm font-bold text-success"><Check className="h-4 w-4" />Complete</button>
                  <button onClick={() => act({ status: "cancelled" })} disabled={busy} data-testid="booking-cancel-btn" className="flex items-center justify-center gap-2 rounded-lg bg-secondary py-2.5 text-sm font-bold text-midnight"><Ban className="h-4 w-4" />Cancel</button>
                  <button onClick={() => setEditing(true)} disabled={busy} data-testid="booking-edit-btn" className="flex items-center justify-center gap-2 rounded-lg border border-border py-2.5 text-sm font-bold text-midnight"><Pencil className="h-4 w-4" />Edit</button>
                </>
              )}
              {(booking.display_status === "cancelled" || booking.display_status === "declined") && (
                <button onClick={() => act({ status: "booked" })} disabled={busy} data-testid="booking-reopen-btn" className="col-span-2 flex items-center justify-center gap-2 rounded-lg border border-border py-2.5 text-sm font-bold text-midnight">Reopen booking</button>
              )}
              {isOwner && booking.display_status !== "pending" && (
                <button onClick={del} disabled={busy} data-testid="booking-delete-btn" className={`flex items-center justify-center gap-2 rounded-lg bg-danger-bg py-2.5 text-sm font-bold text-danger ${active ? "" : "col-span-2"}`}><Trash2 className="h-4 w-4" />Delete</button>
              )}
            </div>
            <button onClick={() => setWaOpen(true)} data-testid="booking-whatsapp-btn"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-[#25D366] bg-[#25D366]/10 py-2.5 text-sm font-bold text-[#128C7E] hover:bg-[#25D366]/20">
              <MessageCircle className="h-4 w-4" />Send via WhatsApp
            </button>
            <WhatsAppLog entityType="booking" entityId={booking.id} refreshKey={waRefresh} />
          </>
        )}
      </div>
      <WhatsAppSendDialog
        open={waOpen} onClose={() => setWaOpen(false)} onSent={() => setWaRefresh((x) => x + 1)}
        customerId={booking.customer?.id}
        customerName={booking.customer?.name || booking.customer_name_online}
        to={booking.customer?.phone || booking.customer_phone_online}
        defaultText={waBookingText(booking, {
          customerName: booking.customer?.name || booking.customer_name_online,
          when: friendly(local), token: booking.public_token,
        })}
        kind="booking_confirmation" entityType="booking" entityId={booking.id}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm">
      <span className="shrink-0 text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right font-semibold text-midnight">{value}</span>
    </div>
  );
}
